import type { MatchState } from '../match/MatchState';
import type { MovementState, PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { clamp, distance, lerp, moveTowards, type Vector2 } from '../match/Vector2';
import { AnchorPolicy } from './AnchorPolicy';
import { DefensiveCoordinator } from './DefensiveCoordinator';
import { FormationRelationships } from './FormationRelationships';
import { GoalkeeperSystem } from './GoalkeeperSystem';
import { isEmergencyIntent, isShapeIntent, type PlayerIntentProposal } from './PlayerIntent';
import { OffensiveCoordinator } from './OffensiveCoordinator';
import { RoleConstraints } from './RoleConstraints';
import { TeamAIDirector } from './TeamAIDirector';
import { TacticalRoleSystem } from './TacticalRoleSystem';

const relativeProgress = (player: PlayerRuntimeState, x: number) => PITCH_GEOMETRY.relativeProgress(player.teamSide, x);
const worldX = (side: 0 | 1, progress: number) => PITCH_GEOMETRY.worldX(side, progress);

export class TacticalPositioning {
  constructor(
    private readonly constraints = new RoleConstraints(),
    private readonly anchors = new AnchorPolicy(),
    private readonly offensive = new OffensiveCoordinator(),
    private readonly defensive = new DefensiveCoordinator(),
    private readonly relationships = new FormationRelationships(),
    private readonly director = new TeamAIDirector(),
    private readonly goalkeepers = new GoalkeeperSystem(),
    private readonly roles = new TacticalRoleSystem(),
  ) {}

  update(state: MatchState, directorAlreadyUpdated = false): void {
    if (!directorAlreadyUpdated) this.director.update(state);
    const owner = state.ball.ownerPlayerId
      ? state.players.find((player) => player.playerId === state.ball.ownerPlayerId)
      : undefined;
    const looseChasers = state.ball.state === 'LOOSE' ? this.looseBallChasers(state) : new Set<string>();
    const formationRelationships = this.relationships.build(state.players);
    for (const player of state.players) {
      player.anchorPosition = this.shapeTarget(player, state, player.teamSide === state.possessionTeam);
      player.recoveryTarget = { ...player.anchorPosition };
    }
    const defensiveSide = owner?.teamSide === 0 ? 1 : 0;
    const defensiveIntents = owner
      ? this.defensive.createIntents(state, owner, state.teamAi[defensiveSide], formationRelationships[defensiveSide])
      : new Map<string, PlayerIntentProposal>();
    const attackingIntents = owner
      ? this.offensive.createIntents(state, owner, state.teamAi[owner.teamSide], formationRelationships[owner.teamSide])
      : new Map<string, PlayerIntentProposal>();

    for (const player of state.players) {
      const ownsBall = owner?.playerId === player.playerId;
      const sameTeamPossession = player.teamSide === state.possessionTeam;
      const looseDistance = distance(player.position, state.ball.position);
      const defensiveIntent = defensiveIntents.get(player.playerId);
      const attackingIntent = attackingIntents.get(player.playerId);
      player.markingTargetId = undefined;
      let proposal: PlayerIntentProposal = {
        movementState: player.positionType === 'GK' ? 'GOALKEEPER_POSITIONING' : 'HOLDING_SHAPE',
        reason: 'KEEP_SHAPE',
        idealTarget: player.anchorPosition,
      };

      if (ownsBall) {
        proposal = { movementState: 'CARRYING_BALL', reason: 'CARRY_BALL', idealTarget: player.contextualTarget, bypassAnchor: true };
      } else if (state.ball.state === 'LOOSE' && looseChasers.has(player.playerId) && looseDistance < 18) {
        proposal = { movementState: 'CHASING_LOOSE_BALL', reason: 'CHASE_LOOSE_BALL', idealTarget: state.ball.position, bypassAnchor: true };
      } else if (state.ball.state === 'PASSING' && state.ball.targetPlayerId === player.playerId) {
        proposal = { movementState: 'TRACKING_RUN', reason: 'RECEIVE_PASS', idealTarget: state.ball.targetPosition, bypassAnchor: true };
      } else if (state.ball.state === 'SHOOTING' && player.positionType === 'GK' && state.ball.flight?.teamSide !== player.teamSide) {
        proposal = { movementState: 'GOALKEEPER_POSITIONING', reason: 'DEFEND_SHOT', idealTarget: state.ball.targetPosition, bypassAnchor: true };
      } else if ((player.bypassedUntil ?? -Infinity) > state.time) {
        // A player beaten by a dribble or stopped by a special block must
        // reset his body and shape before re-engaging. This makes the duel
        // outcome visible instead of allowing an instant recovery tackle.
        proposal = { movementState: 'RECOVERING', reason: 'RECOVER_POSITION', idealTarget: player.recoveryTarget };
      } else if (defensiveIntent) {
        proposal = defensiveIntent;
      } else if (sameTeamPossession && attackingIntent) {
        proposal = attackingIntent;
      } else if (sameTeamPossession) {
        proposal = player.positionType === 'GK'
          ? proposal
          : { movementState: 'SUPPORTING_ATTACK', reason: 'SUPPORT_POSSESSION', idealTarget: player.anchorPosition };
      } else if (distance(player.position, player.anchorPosition) > this.anchors.returnRadius(player.positionType)) {
        proposal = this.returnToShapeProposal(player, state);
      }

      proposal = this.applyRoleMovement(player, proposal, state);
      proposal = this.applyOffsideDiscipline(player, proposal, state, ownsBall);
      proposal = this.resolveIntentLifecycle(player, proposal, state);
      this.applyIntent(player, proposal, state, ownsBall);
      player.currentZone = this.zone(relativeProgress(player, player.position.x));
      if (!ownsBall) player.currentAction = this.actionForMovement(player.movementState);
    }
  }

  private applyRoleMovement(
    player: PlayerRuntimeState,
    proposal: PlayerIntentProposal,
    state: MatchState,
  ): PlayerIntentProposal {
    const role = player.positionType === 'GK' ? null : player.tacticalProfile.assignedRole;
    const definition = this.roles.definition(role);
    if (!definition || isEmergencyIntent(proposal.reason)) return proposal;
    const target = { ...proposal.idealTarget };
    const targetProgress = relativeProgress(player, target.x);
    const homeProgress = relativeProgress(player, player.homePosition.x);

    const holdBias = definition.movementBiases.holdShapeBias ?? 1;
    if (holdBias > 1 && proposal.movementState === 'HOLDING_SHAPE') {
      target.x = worldX(player.teamSide, lerp(targetProgress, homeProgress, Math.min(.5, (holdBias - 1) * 1.8)));
      target.y = lerp(target.y, player.homePosition.y, Math.min(.5, (holdBias - 1) * 1.8));
    }

    const wideBias = definition.movementBiases.widePositionBias ?? 1;
    if (wideBias > 1 && ['MAKING_RUN', 'SUPPORTING_ATTACK', 'PRESSING'].includes(proposal.movementState)) {
      const preferredWideY = player.homePosition.y < 50 ? Math.min(player.homePosition.y, 15) : Math.max(player.homePosition.y, 85);
      target.y = lerp(target.y, preferredWideY, Math.min(.45, (wideBias - 1) * 1.8));
    }

    const boxBias = definition.movementBiases.boxPresenceBias ?? 1;
    if (boxBias > 1 && player.teamSide === state.possessionTeam
      && ['MAKING_RUN', 'SUPPORTING_ATTACK'].includes(proposal.movementState)) {
      const runCeiling = proposal.movementState === 'MAKING_RUN'
        ? Math.min(definition.constraints.maxAdvance, state.config.maximumForwardRunProgress)
        : definition.constraints.maxAdvance;
      target.x = worldX(player.teamSide, clamp(targetProgress + (boxBias - 1) * 18, homeProgress, runCeiling));
      target.y = lerp(target.y, PITCH_GEOMETRY.centre.y, .12);
    } else if (role === 'SECOND_STRIKER' && proposal.movementState === 'SUPPORTING_ATTACK') {
      target.x = worldX(player.teamSide, Math.max(definition.constraints.maxDrop, targetProgress - 4));
    } else if (role === 'ORGANIZER' && proposal.movementState === 'SUPPORTING_ATTACK') {
      target.y = lerp(target.y, state.ball.position.y, .18);
    } else if (role === 'BALL_WINNER' && player.teamSide !== state.possessionTeam
      && ['PRESSING', 'MARKING'].includes(proposal.movementState)) {
      target.x = lerp(target.x, state.ball.position.x, .12);
      target.y = lerp(target.y, state.ball.position.y, .12);
    }

    return { ...proposal, idealTarget: target };
  }

  private shapeTarget(player: PlayerRuntimeState, state: MatchState, attacking: boolean): Vector2 {
    if (player.positionType === 'GK') {
      return this.goalkeepers.positioningTarget(player, state);
    }
    const plan = state.teamAi[player.teamSide];
    const homeProgress = relativeProgress(player, player.homePosition.x);
    const ballProgress = relativeProgress(player, state.ball.position.x);
    const mentality = plan.mentality;
    const width = plan.width;
    const roleBlockWeight = player.positionType === 'DF' ? .76 : player.positionType === 'MF' ? 1 : 1.13;
    // All lines react to territory together, but retain their own band. The
    // result is a moving compact block instead of eleven independent players
    // chasing the ball from one end of the pitch to the other.
    const territorialShift = clamp((ballProgress - 50) * (0.14 + plan.tempo * 0.06), -7.5, 8.5);
    // The defending front line screens midfield instead of either remaining
    // permanently on the last line or collapsing into its own defensive line.
    const possessionShift = attacking
      ? 4.4 + mentality * 4.2 + plan.risk * 1.6
      : (player.positionType === 'FW' ? -7.2 : -3.4) + (plan.defensiveLine - 0.5) * 9;
    const blockProgress = homeProgress + (territorialShift + possessionShift) * roleBlockWeight;
    const ballLateralInfluence = (state.ball.position.y - player.homePosition.y)
      * (player.positionType === 'DF' ? .11 : player.positionType === 'MF' ? .16 : .19)
      * (.75 + width * .45);
    return {
      x: worldX(player.teamSide, blockProgress),
      y: clamp(player.homePosition.y + ballLateralInfluence, 7, 93),
    };
  }

  /** Only the nearest outfielder from each side contests a loose ball.
   * Everyone else keeps shape, so a deflection creates a duel rather than a
   * twelve-player pile-up around one coordinate. */
  private looseBallChasers(state: MatchState): Set<string> {
    const chasers = new Set<string>();
    for (const side of [0, 1] as const) {
      const nearest = state.players
        .filter((player) => player.teamSide === side && (player.positionType !== 'GK'
          || PITCH_GEOMETRY.isInOwnPenaltyArea(side, state.ball.position)))
        .sort((first, second) => distance(first.position, state.ball.position) - distance(second.position, state.ball.position)
          || first.playerId.localeCompare(second.playerId))[0];
      if (nearest && distance(nearest.position, state.ball.position) <= 18) chasers.add(nearest.playerId);
    }
    return chasers;
  }

  private zone(progress: number): string {
    return progress >= PITCH_GEOMETRY.fieldThirds.finalStart
      ? 'FINAL'
      : progress >= PITCH_GEOMETRY.fieldThirds.defensiveEnd ? 'MIDDLE' : 'DEFENSIVE';
  }

  private actionForMovement(state: PlayerRuntimeState['movementState']): PlayerRuntimeState['currentAction'] {
    const actions: Partial<Record<PlayerRuntimeState['movementState'], PlayerRuntimeState['currentAction']>> = {
      PRESSING: 'PRESS', MARKING: 'MARK_PLAYER', TRACKING_RUN: 'MAKE_RUN', RECOVERING: 'RECOVER_POSITION',
      MAKING_RUN: 'MAKE_RUN', SUPPORTING_ATTACK: 'SUPPORT', CHASING_LOOSE_BALL: 'CHASE_LOOSE_BALL',
    };
    return actions[state] ?? 'HOLD_POSITION';
  }

  /**
   * A run remains useful only if it is a legal passing option. Players beyond
   * the second-last opponent actively retreat to the line instead of waiting
   * there for an inevitable offside call; players still onside have their run
   * capped just in front of that moving line.
   */
  private applyOffsideDiscipline(
    player: PlayerRuntimeState,
    proposal: PlayerIntentProposal,
    state: MatchState,
    ownsBall: boolean,
  ): PlayerIntentProposal {
    if (ownsBall || player.positionType === 'GK' || player.teamSide !== state.possessionTeam) return proposal;
    // Only attackers making a run (or already beyond the line) need this
    // restriction. Applying it to every supporting midfielder would flatten
    // the whole attack behind the ball and remove its passing structure.
    const playerProgress = PITCH_GEOMETRY.relativeProgress(player.teamSide, player.position.x);
    if (player.positionType !== 'FW' && proposal.movementState !== 'MAKING_RUN' && proposal.reason !== 'MIDFIELD_ARRIVAL') return proposal;
    const opponentProgress = state.players
      .filter((candidate) => candidate.teamSide !== player.teamSide)
      .map((candidate) => PITCH_GEOMETRY.relativeProgress(player.teamSide, candidate.position.x))
      .sort((first, second) => second - first);
    const secondLastOpponent = opponentProgress[1] ?? opponentProgress[0];
    if (secondLastOpponent === undefined) return proposal;
    const ballProgress = PITCH_GEOMETRY.relativeProgress(player.teamSide, state.ball.position.x);
    const safeLine = clamp(Math.max(ballProgress, secondLastOpponent) - 1.1, 4, 97);
    if (playerProgress > safeLine + .65) {
      return {
        movementState: 'RECOVERING',
        reason: 'RETURN_ONSIDE',
        idealTarget: { x: worldX(player.teamSide, safeLine), y: player.position.y },
        durationSeconds: 2.5,
        bypassAnchor: true,
      };
    }
    const targetProgress = PITCH_GEOMETRY.relativeProgress(player.teamSide, proposal.idealTarget.x);
    if (targetProgress <= safeLine) return proposal;
    return {
      ...proposal,
      idealTarget: { ...proposal.idealTarget, x: worldX(player.teamSide, safeLine) },
      bypassAnchor: proposal.movementState === 'MAKING_RUN' || proposal.bypassAnchor,
    };
  }

  private resolveIntentLifecycle(
    player: PlayerRuntimeState,
    proposal: PlayerIntentProposal,
    state: MatchState,
  ): PlayerIntentProposal {
    if (isEmergencyIntent(proposal.reason)) return proposal;
    if (this.isReturningToShape(player, state)) return this.returnToShapeProposal(player, state);
    const expired = player.intent.expiresAt !== undefined && state.time >= player.intent.expiresAt;
    if (expired && !isShapeIntent(player.intent.reason)) return this.returnToShapeProposal(player, state);
    return proposal;
  }

  private returnToShapeProposal(player: PlayerRuntimeState, state: MatchState): PlayerIntentProposal {
    return {
      movementState: player.positionType === 'GK' ? 'GOALKEEPER_POSITIONING' : 'RECOVERING',
      reason: 'RETURN_TO_SHAPE',
      idealTarget: player.anchorPosition,
      durationSeconds: state.config.returnToShapeMinimumSeconds,
    };
  }

  private isReturningToShape(player: PlayerRuntimeState, state: MatchState): boolean {
    if (player.intent.reason !== 'RETURN_TO_SHAPE') return false;
    const minimumReturnElapsed = player.intent.expiresAt === undefined || state.time >= player.intent.expiresAt;
    const reachedAnchor = distance(player.position, player.anchorPosition) <= this.anchors.returnRadius(player.positionType);
    return !minimumReturnElapsed || !reachedAnchor;
  }

  private applyIntent(
    player: PlayerRuntimeState,
    proposal: PlayerIntentProposal,
    state: MatchState,
    ownsBall: boolean,
  ): void {
    player.movementState = proposal.movementState;
    player.markingTargetId = proposal.markingTargetId;
    const anchoredTarget = this.anchors.soften(
      player,
      proposal.idealTarget,
      state.config.anchorSoftness,
      proposal.bypassAnchor ?? false,
    );
    const legalTarget = this.constraints.constrain(player, anchoredTarget);
    const speedRatio = Math.pow(Math.max(0, player.stats.speed / 100), state.config.speedAttributeExponent);
    const baseSpeed = state.config.minPlayerSpeed
      + (state.config.maxPlayerSpeed - state.config.minPlayerSpeed) * speedRatio;
    const horizon = Math.max(state.config.movementStepSeconds, state.config.reachableTargetHorizonSeconds);
    const reachableTarget = moveTowards(player.position, legalTarget, baseSpeed * horizon * 1.2);
    const sameIntent = player.intent.reason === proposal.reason
      && (player.intent.expiresAt === undefined || state.time < player.intent.expiresAt);
    const createdAt = sameIntent ? player.intent.createdAt : state.time;
    const expiresAt = sameIntent
      ? player.intent.expiresAt
      : proposal.durationSeconds === undefined ? undefined : state.time + proposal.durationSeconds;

    if (!sameIntent && state.time > 0) {
      const responsiveness = Math.max(0, (player.stats.control * .65 + player.stats.speed * .35) / 100);
      const delay = state.config.minimumReactionSeconds
        + (state.config.maximumReactionSeconds - state.config.minimumReactionSeconds) * (1 - responsiveness);
      player.reactionSecondsRemaining = proposal.reason === 'DEFEND_SHOT' ? delay * .5 : delay;
    }

    player.intent = {
      movementState: proposal.movementState,
      reason: proposal.reason,
      idealTarget: { ...proposal.idealTarget },
      reachableTarget: { ...reachableTarget },
      createdAt,
      expiresAt,
      markingTargetId: proposal.markingTargetId,
    };
    player.tacticalTarget = { ...legalTarget };
    if (!ownsBall || player.currentAction !== 'DRIBBLE') player.contextualTarget = { ...legalTarget };
    player.targetPosition = { ...reachableTarget };
  }
}
